import { useMemo } from "react"

import {
  useAllAnnotationsByOrgTransport,
  useAnnotationsWithMetricOutputsTransport,
} from "@/human-feedback/transport"

import type { MetricOutputsByMetric } from "@/types/api/MetricOutputsByMetric"

const MAX_ANNOTATIONS = 25
const EMPTY_METRIC_OUTPUTS: MetricOutputsByMetric[string] = {}

export function usePromptFeedbackData(metricId?: string) {
  const {
    data: annotationsByMetric,
    isError: isAnnotationsError,
    isPending: isAnnotationsPending,
  } = useAllAnnotationsByOrgTransport({
    metricId,
    filterArchived: true,
    enabled: !!metricId,
  })

  const allAnnotations = useMemo(
    () => annotationsByMetric?.[metricId as string] ?? [],
    [annotationsByMetric, metricId],
  )
  const relatedAnnotations = useMemo(
    () =>
      allAnnotations
        .slice()
        .sort(
          (first, second) =>
            new Date(second.last_updated_at ?? 0).getTime() -
            new Date(first.last_updated_at ?? 0).getTime(),
        )
        .slice(0, MAX_ANNOTATIONS),
    [allAnnotations],
  )
  const relatedSimulationOutputIds = useMemo(
    () =>
      Array.from(
        new Set(
          relatedAnnotations.map(
            (annotation) => annotation.simulation_output_id,
          ),
        ),
      ),
    [relatedAnnotations],
  )

  const {
    data: pairedAnnotationData,
    isError: isMetricOutputsError,
    isPending: isMetricOutputsPending,
  } = useAnnotationsWithMetricOutputsTransport({
    metricId,
    simulationOutputIds: relatedSimulationOutputIds,
    filterArchived: true,
    includeMetricOutputDetails: true,
    includeSubvalues: false,
    includeAudioLengths: false,
    enabled: !!metricId && relatedSimulationOutputIds.length > 0,
  })

  const metricOutputs = useMemo(
    () =>
      pairedAnnotationData?.metricOutputs[metricId as string] ??
      EMPTY_METRIC_OUTPUTS,
    [metricId, pairedAnnotationData],
  )
  const isFeedbackDataLoading =
    (!!metricId && isAnnotationsPending) ||
    (relatedSimulationOutputIds.length > 0 && isMetricOutputsPending)
  const feedbackUnavailableReason =
    isAnnotationsError || isMetricOutputsError
      ? "Feedback annotations or their paired AI outputs could not be loaded. General suggestions are still available."
      : undefined

  return {
    allAnnotations,
    feedbackUnavailableReason,
    isFeedbackDataLoading,
    metricOutputs,
    relatedAnnotations,
  }
}
